import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import { tripService } from '../../services/tripService';
import { userProfileService } from '../../services/userProfileService';
import Header from '../../components/navigation/Header';
import MobileNavigation from '../../components/navigation/MobileNavigation';
import TripDetailsForm from './components/TripDetailsForm';
import StorageOptionsForm from './components/StorageOptionsForm';
import CookingEquipmentForm from './components/CookingEquipmentForm';
import TripLimitationNotice from './components/TripLimitationNotice';
import FormActions from './components/FormActions';

const TripSetup = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [loadingTrip, setLoadingTrip] = useState(true);
  const [existingTrip, setExistingTrip] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [formData, setFormData] = useState({
    tripName: '',
    crewSize: '2',
    duration: '3',
    hasFridge: true,
    hasFreezer: false,
    hasIceCooler: false,
    hasStove: true,
    hasOven: false,
    hasGrill: false,
    hasMicrowave: false,
    hasAirFryer: false,
    dietaryRestrictions: '',
    isPremium: false
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    const loadExistingTrip = async () => {
      if (!user?.id) return;
      
      setLoadingTrip(true);
      
      // Load user profile to get subscription status
      const { data: profileData } = await userProfileService?.getProfile(user?.id);
      if (profileData) {
        setUserProfile(profileData);
      }
      
      const { data, error } = await tripService?.getActiveTrip(user?.id);
      
      if (!error && data) {
        setExistingTrip(data);
        setFormData({
          tripName: data?.tripName || '',
          crewSize: String(data?.crewSize || 2),
          duration: String(data?.duration || 3),
          hasFridge: data?.hasFridge ?? true,
          hasFreezer: data?.hasFreezer ?? false,
          hasIceCooler: data?.hasIceCooler ?? false,
          hasStove: data?.hasStove ?? true,
          hasOven: data?.hasOven ?? false,
          hasGrill: data?.hasGrill ?? false,
          hasMicrowave: data?.hasMicrowave ?? false,
          hasAirFryer: data?.hasAirFryer ?? false,
          dietaryRestrictions: data?.dietaryRestrictions || '',
          isPremium: profileData?.subscriptionStatus === 'premium'
        });
      } else {
        // No existing trip, set isPremium based on subscription status
        setFormData(prev => ({
          ...prev,
          isPremium: profileData?.subscriptionStatus === 'premium'
        }));
      }
      
      setLoadingTrip(false);
    };

    loadExistingTrip();
  }, [user]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.tripName?.trim()) {
      newErrors.tripName = 'Trip name is required';
    } else if (formData?.tripName?.trim()?.length < 3) {
      newErrors.tripName = 'Trip name must be at least 3 characters';
    }

    if (!formData?.crewSize) {
      newErrors.crewSize = 'Crew size is required';
    }

    if (!formData?.duration) {
      newErrors.duration = 'Trip duration is required';
    }

    const hasAnyCookingEquipment = formData?.hasStove || formData?.hasOven || formData?.hasGrill || formData?.hasMicrowave || formData?.hasAirFryer;
    if (!hasAnyCookingEquipment) {
      newErrors.cookingEquipment = 'Select at least one cooking equipment';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    
    // Normalize null-equivalent inputs for dietary restrictions
    let normalizedValue = value;
    if (name === 'dietaryRestrictions') {
      const trimmedValue = value?.trim()?.toLowerCase();
      const nullEquivalents = ['none', 'n/a', '0', 'not applicable', 'not known', 'unknown'];
      if (nullEquivalents?.includes(trimmedValue)) {
        normalizedValue = '';
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: normalizedValue
    }));

    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    const tripData = {
      userId: user?.id,
      tripName: formData?.tripName,
      crewSize: formData?.crewSize,
      duration: formData?.duration,
      startDate: formData?.startDate,
      status: 'active', // Explicitly set status to 'active' so getActiveTrip() can find it
      hasRefrigerator: formData?.hasRefrigerator,
      hasFreezer: formData?.hasFreezer,
      hasGrill: formData?.hasGrill,
      hasAirFryer: formData?.hasAirFryer,
      hasMicrowave: formData?.hasMicrowave,
      hasStove: formData?.hasStove,
      hasOven: formData?.hasOven,
      isPremium: formData?.isPremium,
      completedDays: [] // Reset completed days when creating/updating trip
    };

    let result;
    if (existingTrip) {
      result = await tripService?.updateTrip(existingTrip?.id, tripData);
    } else {
      result = await tripService?.createTrip(tripData);
    }

    setIsLoading(false);

    if (result?.error) {
      setErrors({ tripName: result?.error?.message || 'Failed to save trip' });
    } else {
      // Wait a moment to ensure database write is complete and queryable
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Trip created successfully - navigate to inventory with trip data
      navigate('/inventory-management', { 
        state: { tripCreated: true, tripData: result?.data },
        replace: true 
      });
    }
  };

  const handleCancel = () => {
    navigate('/meal-planning');
  };

  const isFormValid = formData?.tripName?.trim()?.length >= 3 && 
                      formData?.crewSize && 
                      formData?.duration &&
                      (formData?.hasStove || formData?.hasOven || formData?.hasGrill || formData?.hasMicrowave || formData?.hasAirFryer);

  if (loadingTrip) {
    return (
      <>
        <Helmet>
          <title>Trip Setup - GalleyPlan</title>
        </Helmet>
        <Header />
        <div className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <p className="text-lg text-muted-foreground">Loading trip data...</p>
          </div>
        </div>
        <MobileNavigation />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Trip Setup - GalleyPlan</title>
        <meta name="description" content="Configure your coastal cruising parameters for accurate meal planning and inventory management" />
      </Helmet>

      <Header />

      <div className="min-h-screen pt-16 pb-20 lg:pb-8" style={{ backgroundColor: 'var(--color-background)' }}>
        <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
          <div className="mb-6 md:mb-8">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-semibold mb-3 md:mb-4" style={{ color: 'var(--color-text-primary)' }}>
              Trip Setup
            </h1>
            <p className="text-base md:text-lg" style={{ color: 'var(--color-text-secondary)' }}>
              Configure your coastal cruising parameters to enable accurate meal planning and inventory management for your sailing adventure.
            </p>
          </div>

          <TripLimitationNotice hasExistingTrip={!!existingTrip} />

          <div className="mt-6 md:mt-8 space-y-8 md:space-y-10 lg:space-y-12">
            <div className="p-6 md:p-8 rounded-lg" style={{ backgroundColor: 'var(--color-card)' }}>
              <TripDetailsForm
                formData={formData}
                errors={errors}
                onChange={handleInputChange}
                userProfile={userProfile}
              />
            </div>

            <div className="p-6 md:p-8 rounded-lg" style={{ backgroundColor: 'var(--color-card)' }}>
              <StorageOptionsForm
                formData={formData}
                onChange={handleInputChange}
              />
            </div>

            <div className="p-6 md:p-8 rounded-lg" style={{ backgroundColor: 'var(--color-card)' }}>
              <CookingEquipmentForm
                formData={formData}
                errors={errors}
                onChange={handleInputChange}
              />
            </div>

            <div className="pt-6 border-t" style={{ borderColor: 'var(--color-border)' }}>
              <FormActions
                onSubmit={handleSubmit}
                onSave={handleSubmit}
                onCancel={handleCancel}
                isLoading={isLoading}
                isSaving={isLoading}
                isValid={isFormValid}
                hasActiveTrip={!!existingTrip}
              />
            </div>
          </div>
        </div>
      </div>

      <MobileNavigation />
    </>
  );
};

export default TripSetup;